import type { CatalogSport, CatalogSportsbook, SportDetails } from './api'
import type { AdminSubAffiliate, EventCalendar as EventCalendarPayload } from './admin/api'
import type { Role } from './useAuthSession'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// The AVAILABILITY MAP card mounts the real SDK component (not a portal
// re-implementation) so the preview is exactly what partners embed. Its
// stylesheet's .am-* classes are globally unique, so importing it outside
// Shadow DOM is safe; the :host block is inert here and .ew-map-body
// supplies the font sizing instead.
import ApprovalMapWidget from '../../src/approval-map-sdk/ApprovalMapWidget'
import { EUROPE_COUNTRIES, EUROPE_MAP_VIEWBOX, WORLD_COUNTRIES, WORLD_MAP_VIEWBOX, WORLD_PLACE_BY_ID } from '../../src/approval-map-sdk/worldMapPaths'
// Sportsbook pages mount the real distribution / approvals SDK components in
// `sportsbook:` mode (they self-fetch from API_BASE); their .ew-* classes are
// the same ones the portal-side twins use, so the portal stylesheet covers them.
import SdkApprovalsWidget from '../../src/approvals-sdk/ApprovalsWidget'
import SdkDistributionWidget from '../../src/distribution-sdk/DistributionWidget'
import { fetchEventCalendar, fetchSubAffiliates } from './admin/api'
import { API_BASE, assetUrl, categoryLabel, fetchCatalog, fetchSportDetails, fetchSportsbookCatalog, GROUP_ORDER } from './api'
import { SportSheet, useSportSheetOpener } from './SportSheet'
import ApprovalsWidget from './embed/ApprovalsWidget'
import DistributionWidget from './embed/DistributionWidget'
import EventCalendar, { CALENDAR_DAY_OPTIONS } from './EventCalendar'
import { highlightLine } from './highlight'
import { buildApprovalMapSnippet, buildApprovalsSnippet, buildDistributionSnippet, buildPreviewDoc, buildSnippet, buildSportsbookApprovalsSnippet, buildSportsbookDistributionSnippet, buildSportsbookMapSnippet, DEFAULT_BOOKS } from './snippet'
import { hasRole } from './useAuthSession'
import { useResizable } from './useResizable'
import '../../src/approval-map-sdk/styles.css'

// Customize tab is hidden for now — the SDK doesn't yet act on theme/size/
// format/sportsbook options, so showing the controls would over-promise.
// State for the customize options stays in place so when the tab returns
// the snippet shape is already wired through.
type Tab = 'preview' | 'embed'

const ACCORDION_STORAGE_KEY = 'odds-portal-embed-accordion'

// EVENT CALENDAR (Overview week grid + per-sport UPCOMING EVENTS panel) —
// built 2026-10-12, hidden the same day, reworked as a grid 2026-10-21,
// owner-only for an hour, then opened to every signed-in user the same day
// (Cole: "make available to all"). Matches the API route (/admin baseline
// is user+); roles below 'user' see neither panel and never fetch.
const EVENT_CALENDAR_MIN_ROLE: Role = 'user'

function readInitialExpanded(): Set<string> {
  if (typeof window === 'undefined') return new Set(['MOTORSPORT'])
  try {
    const stored = localStorage.getItem(ACCORDION_STORAGE_KEY)
    if (stored != null) {
      const parsed = JSON.parse(stored) as unknown
      // Single-open accordion — sessions stored before the multi-open
      // behavior was retired may hold several categories; keep the first.
      if (Array.isArray(parsed)) return new Set(parsed.filter(x => typeof x === 'string').slice(0, 1))
    }
  }
  catch {
    /* ignore */
  }
  return new Set(['MOTORSPORT'])
}

// Injected into ApprovalMapWidget to switch on the Global / Europe / List View
// tabs. The widget takes this as a prop rather than importing it so the live SDK
// bundle can tree-shake the geometry; here in the portal we always have it.
const WORLD = {
  global: { viewBox: WORLD_MAP_VIEWBOX, countries: WORLD_COUNTRIES },
  europe: { viewBox: EUROPE_MAP_VIEWBOX, countries: EUROPE_COUNTRIES },
  placeById: WORLD_PLACE_BY_ID,
}

// ── Native odds dashboard (overview) ────────────────────────────────
// The overview mounts the REAL widget SDK directly on the page — no
// iframe. The bundle is same-origin (/widget.umd.js, preloaded by
// index.html); loaded once on demand.
interface OddsWidgetGlobal {
  init: (el: HTMLElement, config: Record<string, unknown>) => { destroy: () => void }
}
let oddsWidgetScript: Promise<void> | null = null
function ensureOddsWidgetScript(): Promise<void> {
  if ((window as { OddsWidget?: OddsWidgetGlobal }).OddsWidget)
    return Promise.resolve()
  oddsWidgetScript ??= new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `${API_BASE}/widget.umd.js`
    s.onload = () => resolve()
    s.onerror = () => {
      oddsWidgetScript = null
      reject(new Error('widget bundle failed to load'))
    }
    document.head.appendChild(s)
  })
  return oddsWidgetScript
}

// Structural skin appended into the widget's open shadow roots (the
// mount's and the shared bet-slip overlay's). Colors come from --lp-*
// variables set in styles.css (.ew-dash-mount / #odds-widget-overlays) —
// the widget runs theme:'dark', whose palette is fully var-driven, so the
// portal palette flows in and flips live with the theme toggle. Book
// logos + brand colors are inline styles/images and pass through. The
// max-width kill overrides the widget's inline 1440px cap so the board
// fills the page column.
const NATIVE_SKIN_CSS
  = '*,*::before,*::after{border-radius:0 !important;box-shadow:none !important}'
    + '*{font-family:\'Inter\',system-ui,sans-serif !important}'
    + 'div[style*="max-width"]{max-width:none !important}'

function injectNativeSkin(host: Element | null): void {
  const root = host?.shadowRoot
  if (!root || root.querySelector('style[data-asd-native]'))
    return
  const style = document.createElement('style')
  style.setAttribute('data-asd-native', '')
  style.textContent = NATIVE_SKIN_CSS
  root.appendChild(style)
}

// EMBED CODE panel shared by every card — line count, COPY, numbered code.
function EmbedCode({ snippet, copied, onCopy }: { snippet: string, copied: boolean, onCopy: () => void }) {
  return (
    <div className="ew-embed-body">
      <div className="ew-embed-meta">
        <span className="b-mono-dim">{snippet.split('\n').length} LINES</span>
        <button
          type="button"
          onClick={onCopy}
          className={`b-btn b-btn-primary b-btn-tiny${copied ? ' is-success' : ''}`}
        >
          {copied ? '✓ COPIED' : 'COPY'}
        </button>
      </div>
      <pre className="b-code">
        {snippet.split('\n').map((line, i) => (
          <div key={i} className="b-code-line">
            <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
            <span className="b-code-text">{highlightLine(line, i)}</span>
          </div>
        ))}
      </pre>
    </div>
  )
}

// Sidebar / sheet selection keys: 'overview' | 'general' | <sport slug> |
// 'book:<sportsbook slug>'.
const BOOK_PREFIX = 'book:'

export default function App({ isDark = false, role = null }: { isDark?: boolean, role?: Role | null }) {
  // Auth + theme are owned by the top-level Router in main.tsx — App is
  // mounted only when there's a valid session, and the shared Header renders
  // on top of us. App's job is just the embed-page body. `isDark` comes down
  // as a prop (not a second useTheme() — the hook's storage event only fires
  // cross-tab, so a sibling instance would go stale on same-tab toggles).

  // Full embeddable catalog — every sports row with a slug, flagged
  // configured (odds config exists) + hasData (live odds now). Catalog-only
  // sports still embed distribution / approvals / availability map.
  const [catalog, setCatalog] = useState<CatalogSport[] | null>(null)
  // Sportsbook-keyed embeds — every book that carries a sport or has
  // availability data (GET /api/v1/sportsbooks-catalog). Non-fatal: a failed
  // fetch just leaves the SPORTSBOOKS group out of the sidebar.
  const [bookCatalog, setBookCatalog] = useState<CatalogSportsbook[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  // 'overview' (the landing page) | 'general' | a sport slug.
  const [selected, setSelected] = useState<string>('overview')
  const [tab, setTab] = useState<Tab>('preview')
  const [distTab, setDistTab] = useState<Tab>('preview')
  const [apprTab, setApprTab] = useState<Tab>('preview')
  const [mapTab, setMapTab] = useState<Tab>('preview')
  // One copied-flash key for every COPY button on the page ('odds', 'dist',
  // 'appr', 'map', 'dash') — replaces five parallel boolean states.
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  // TOP 5 chip on the DISTRIBUTION card — caps preview + snippet at the
  // top 5 books (API carrier-priority order). Sticky across sport switches,
  // like the card tab states.
  const [distTop5, setDistTop5] = useState(false)
  // The map SDK component signals "no data" via onEmpty instead of
  // rendering; the card swaps in a placeholder. Reset per sport.
  const [mapEmpty, setMapEmpty] = useState(false)
  useEffect(() => { setMapEmpty(false) }, [selected])
  const [sheetOpen, setSheetOpen] = useState(false)
  // EVENT CALENDAR — the overview's upcoming-events grid (AltSportsData
  // API through the authed /admin/event-calendar proxy). One 30-day pull per
  // overview visit, sliced client-side by the NEXT 7D / 14D / 30D chips.
  // Non-fatal: a failed fetch renders as a note inside the column.
  const [calendar, setCalendar] = useState<EventCalendarPayload | null>(null)
  const [calendarError, setCalendarError] = useState<string | null>(null)
  const [calendarDays, setCalendarDays] = useState<number>(CALENDAR_DAY_OPTIONS[1])
  const showCalendar = hasRole(role, EVENT_CALENDAR_MIN_ROLE)
  // The header's mobile-menu SPORTS row opens this page's sheet.
  useSportSheetOpener(useCallback(() => setSheetOpen(true), []))

  // The header's EMBED tab always lands on the embed home — route.navigate
  // no-ops when we're already on '/', so the Header dispatches this event
  // and we reset the selection ourselves.
  useEffect(() => {
    const onHome = () => setSelected('overview')
    window.addEventListener('odds-portal:embed-home', onHome)
    return () => window.removeEventListener('odds-portal:embed-home', onHome)
  }, [])

  // Sub-affiliate EMBED AS picker — available to every signed-in role.
  // '' = HOUSE (our links); a flag re-bakes every snippet on the page with
  // subAffiliate and points the previews at the ?aff= server swap so
  // tiles/links render exactly what the flagged embed serves. The GET
  // endpoint allows any signed-in user (writes stay affiliate+). The picker
  // itself only appears on sport pages at least one sub-affiliate promotes
  // (sub_affiliate_sports) — eligibility + effective flag derive below once
  // `sport` is known.
  const [subs, setSubs] = useState<AdminSubAffiliate[] | null>(null)
  const [aff, setAff] = useState('')
  useEffect(() => {
    fetchSubAffiliates().then(setSubs).catch(() => setSubs(null))
  }, [])

  // Track when the iframe finishes loading so we can show a skeleton over
  // the slot until the widget bundle + first odds payload land. The odds
  // iframe's reset effect lives below, after the effective sub-affiliate
  // flag is derived (its srcDoc depends on it).
  const [iframeLoaded, setIframeLoaded] = useState(false)

  // Overview dashboard — real SDK mounted natively on the page (house
  // links only). Destroyed when the overview unmounts; the skin-injection
  // loop keeps retrying briefly because the bet-slip overlay's shadow host
  // (#odds-widget-overlays) is created lazily on document.body.
  const dashMount = useRef<HTMLDivElement | null>(null)
  const isOverviewSelected = selected === 'overview'
  useEffect(() => {
    if (!isOverviewSelected)
      return
    let cancelled = false
    let instance: { destroy: () => void } | null = null
    let timer: number | undefined
    ensureOddsWidgetScript()
      .then(() => {
        if (cancelled || !dashMount.current)
          return
        const sdk = (window as { OddsWidget?: OddsWidgetGlobal }).OddsWidget!
        instance = sdk.init(dashMount.current, { mode: 'dashboard', apiBase: API_BASE, theme: 'dark' })
        let tries = 0
        timer = window.setInterval(() => {
          injectNativeSkin(dashMount.current)
          injectNativeSkin(document.getElementById('odds-widget-overlays'))
          if (++tries > 100)
            window.clearInterval(timer)
        }, 300)
      })
      .catch(() => { /* widget bundle unreachable — the overview still renders */ })
    return () => {
      cancelled = true
      if (timer)
        window.clearInterval(timer)
      instance?.destroy()
    }
  }, [isOverviewSelected])

  const isOverview = selected === 'overview'

  useEffect(() => {
    if (!showCalendar || !isOverview)
      return
    let cancelled = false
    setCalendarError(null)
    fetchEventCalendar(30)
      .then((payload) => { if (!cancelled) setCalendar(payload) })
      .catch((err: unknown) => { if (!cancelled) setCalendarError(err instanceof Error ? err.message : String(err)) })
    return () => { cancelled = true }
  }, [isOverview, showCalendar])
  const isGeneral = selected === 'general'
  const isBook = selected.startsWith(BOOK_PREFIX)
  const bookSlug = isBook ? selected.slice(BOOK_PREFIX.length) : null

  // Per-sport details (state approvals + carriers) feed the APPROVALS and
  // DISTRIBUTION cards. The fetch effect lives below — it depends on the
  // effective sub-affiliate flag (the server swaps carrier links per ?aff=).
  const [details, setDetails] = useState<SportDetails | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)

  const sidebar = useResizable({
    defaultWidth: 320,
    minWidth: 220,
    maxWidth: 560,
    storageKey: 'odds-portal-sidebar-width',
  })

  const [expanded, setExpanded] = useState<Set<string>>(readInitialExpanded)

  function toggleExpanded(cat: string) {
    setExpanded((prev) => {
      // Single-open accordion: opening a group closes whichever was open;
      // clicking the open group closes it (all-closed is a valid state).
      const next = new Set(prev.has(cat) ? [] : [cat])
      try {
        localStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify([...next]))
      }
      catch {
        /* ignore */
      }
      return next
    })
  }

  // Snippet currently emits defaults only — Customize tab is hidden, so
  // theme/size/format/books stay at their default values until we ship
  // SDK support for them.
  const theme = 'default' as const
  const size = 'auto' as const
  const format = 'american' as const
  const books = DEFAULT_BOOKS

  useEffect(() => {
    fetchCatalog()
      .then(setCatalog)
      .catch((e: Error) => setError(e.message))
    fetchSportsbookCatalog()
      .then(setBookCatalog)
      .catch(() => setBookCatalog([]))
  }, [])

  // GENERAL is a pseudo-entry: not a sport, but /api/v1/sports/general/details
  // and /api/v1/approval-maps/general exist, so the DISTRIBUTION + AVAILABILITY
  // MAP cards work operator-level. LIVE ODDS + APPROVALS are hidden for it.
  const sport = useMemo(
    () => isGeneral
      ? { id: null, slug: 'general', name: 'General', category: null, logo_url: null, configured: false, hasData: false } satisfies CatalogSport
      : catalog?.find(s => s.slug === selected) ?? null,
    [catalog, selected, isGeneral],
  )
  // Sports with an odds config keep the LIVE ODDS card even while their
  // board is empty (the widget renders its own no-odds state). Catalog-only
  // sports have no odds config — the availability map takes the hero slot.
  const hasOdds = !isGeneral && !isOverview && !isBook && (sport?.configured ?? false)

  const book = useMemo(
    () => (bookSlug ? bookCatalog?.find(b => b.slug === bookSlug) ?? null : null),
    [bookCatalog, bookSlug],
  )

  // Sub-affiliates that promote the selected sport (their PROMOTED SPORTS
  // list on /affiliate). The EMBED AS picker only offers those, and only on
  // sport pages — overview and GENERAL always embed house links. A flag
  // picked on one sport carries to other sports that sub promotes and falls
  // back to HOUSE everywhere else (affFlag is the effective value).
  const pageSubs = useMemo(() => {
    if (!subs || isOverview || isGeneral)
      return []
    // Sportsbook pages: nothing links out (league logos are plain — Cole
    // 2026-10-07), so there is no EMBED AS picker there.
    if (isBook)
      return []
    if (!sport || sport.id == null)
      return []
    return subs.filter(s => (s.sub_affiliate_sports ?? []).some(x => x.sport_id === sport.id))
  }, [subs, isOverview, isGeneral, isBook, book, sport])
  const affFlag = pageSubs.some(s => s.slug === aff) ? aff : undefined

  useEffect(() => { setIframeLoaded(false) }, [selected, isDark, affFlag])

  useEffect(() => {
    if (!selected || isOverview || isBook) return
    setDetailsLoading(true)
    fetchSportDetails(selected, affFlag)
      .then(setDetails)
      .catch(() => setDetails(null))
      .finally(() => setDetailsLoading(false))
  }, [selected, isOverview, isBook, affFlag])

  const filtered = useMemo(() => {
    if (!catalog)
      return []
    const q = filter.trim().toLowerCase()
    if (!q)
      return catalog
    return catalog.filter(s =>
      s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    )
  }, [catalog, filter])

  // Group sports by category for the sidebar accordion. Categories follow
  // the shared GROUP_ORDER; sports sort alphabetically within each group.
  const grouped = useMemo(() => {
    const m = new Map<string, CatalogSport[]>()
    for (const label of GROUP_ORDER) m.set(label, [])
    for (const s of filtered) {
      const k = categoryLabel(s.category)
      if (!m.has(k))
        m.set(k, [])
      m.get(k)!.push(s)
    }
    for (const [k, v] of m) {
      if (v.length === 0)
        m.delete(k)
      else v.sort((a, b) => a.name.localeCompare(b.name))
    }
    return m
  }, [filtered])

  // When actively searching, force-expand all matching groups so hits
  // aren't hidden behind a closed accordion. Empty search → defer to
  // the persisted accordion state.
  const isSearching = filter.trim().length > 0
  const effectiveExpanded = isSearching
    ? new Set(grouped.keys())
    : expanded

  // Coverage buckets for the overview stats + mobile chip strip.
  const liveSports = useMemo(() => (catalog ?? []).filter(s => s.hasData), [catalog])
  const quietSports = useMemo(() => (catalog ?? []).filter(s => s.configured && !s.hasData), [catalog])

  const snippet = useMemo(
    () => sport && hasOdds ? buildSnippet(sport.slug, { theme, size, format, books }, affFlag) : '',
    [sport, hasOdds, theme, size, format, books, affFlag],
  )

  // previewDoc depends on `isDark`: the widget reads data-asd-theme once at
  // mount, so showing the dark theme requires a fresh iframe doc. The toggle
  // therefore reloads the preview (and refetches odds) — the price of the
  // preview matching what a dark host page actually renders.
  const previewDoc = useMemo(
    () => sport && hasOdds ? buildPreviewDoc(sport.slug, isDark, affFlag) : '',
    [sport, hasOdds, isDark, affFlag],
  )

  const distSnippet = useMemo(
    () => sport ? buildDistributionSnippet(sport.slug, distTop5 ? 5 : undefined, affFlag) : '',
    [sport, distTop5, affFlag],
  )

  const apprSnippet = useMemo(
    () => sport ? buildApprovalsSnippet(sport.slug) : '',
    [sport],
  )

  const mapSnippet = useMemo(
    () => sport ? buildApprovalMapSnippet(sport.slug, affFlag) : '',
    [sport, affFlag],
  )

  // Sportsbook page snippets — same bundles, `sportsbook:` option.
  const bookDistSnippet = useMemo(
    () => book ? buildSportsbookDistributionSnippet(book.slug, distTop5 ? 5 : undefined) : '',
    [book, distTop5],
  )
  const bookApprSnippet = useMemo(
    () => book ? buildSportsbookApprovalsSnippet(book.slug) : '',
    [book],
  )
  const bookMapSnippet = useMemo(
    () => book ? buildSportsbookMapSnippet(book.slug) : '',
    [book],
  )

  const filteredBooks = useMemo(() => {
    if (!bookCatalog)
      return []
    const q = filter.trim().toLowerCase()
    return q
      ? bookCatalog.filter(b => b.name.toLowerCase().includes(q) || b.slug.includes(q))
      : bookCatalog
  }, [bookCatalog, filter])
  const showBooksGroup = filteredBooks.length > 0
  const booksGroupOpen = isSearching || expanded.has('SPORTSBOOKS')

  function bookKindTag(b: CatalogSportsbook) {
    if (b.kind === 'dfs')
      return <span className="b-mono-dim b-tiny">DFS</span>
    if (b.kind === 'prediction')
      return <span className="b-mono-dim b-tiny">PREDICTION</span>
    return <span className="b-mono-dim b-tiny">{b.sports_count} SPORTS</span>
  }

  async function copyText(key: string, text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(k => (k === key ? null : k)), 1800)
    }
    catch {
      setCopiedKey(null)
    }
  }

  // Right-side sidebar/sheet indicator: live odds → dot, configured but
  // quiet → nothing, catalog-only → MAP ONLY (the map is that sport's
  // hero embed).
  function sportIndicator(s: CatalogSport) {
    if (s.hasData)
      return <span className="b-dot b-dot-on" />
    if (s.configured)
      return null
    return <span className="b-mono-dim b-tiny">MAP ONLY</span>
  }

  // EMBED AS control (all signed-in roles) — rendered in a sport page's
  // title block only when at least one sub-affiliate promotes that sport.
  const affPicker = pageSubs.length > 0
    ? (
        <label className="ad-field ew-aff-field">
          <span className="b-mono-dim b-tiny">EMBED AS</span>
          <select
            className="ad-input"
            value={affFlag ?? ''}
            onChange={e => setAff(e.target.value)}
          >
            <option value="">ASD - OWNED</option>
            {pageSubs.map(s => (
              <option key={s.id} value={s.slug}>
                {s.name}
                {' - SHARED'}
              </option>
            ))}
          </select>
        </label>
      )
    : null

  if (error) {
    return (
      <main className="b-main">
        <p className="b-mono" style={{ color: '#dc2626' }}>
          ERR / catalog fetch failed: {error}
        </p>
      </main>
    )
  }

  return (
    <>
      {/* MOBILE SPORT CHIP STRIP — hidden on desktop */}
      <div className="b-chips">
        <div className="b-chips-scroll">
          <button
            type="button"
            onClick={() => setSelected('overview')}
            className={`b-chip${isOverview ? ' is-active' : ''}`}
          >
            <span className="b-mono-dim b-tiny">⌂</span>
            <span className="b-chip-name">Overview</span>
          </button>
          <button
            type="button"
            onClick={() => setSelected('general')}
            className={`b-chip${isGeneral ? ' is-active' : ''}`}
          >
            <span className="b-mono-dim b-tiny">◈</span>
            <span className="b-chip-name">General</span>
          </button>
          {liveSports.map((s, i) => {
            const active = s.slug === selected
            return (
              <button
                type="button"
                key={s.slug}
                onClick={() => setSelected(s.slug)}
                className={`b-chip${active ? ' is-active' : ''}`}
              >
                <span className="b-mono-dim b-tiny">{String(i + 1).padStart(2, '0')}</span>
                <span className="b-chip-name">{s.name}</span>
                <span className="b-dot b-dot-on" />
              </button>
            )
          })}
        </div>
        <button
          type="button"
          className="b-all-btn"
          onClick={() => setSheetOpen(true)}
          aria-label="All sports"
        >
          ▾
        </button>
      </div>

      {/* MAIN GRID */}
      <div className="b-grid" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
        {/* SIDEBAR — hidden on mobile */}
        <aside className="b-sidebar">
          {/* OVERVIEW + GENERAL — pinned side by side above the search bar;
              neither belongs to a sport category. */}
          <div className="b-sidebar-pins">
            <button
              type="button"
              onClick={() => setSelected('overview')}
              className={`b-chip${isOverview ? ' is-active' : ''}`}
            >
              <span className="b-mono-dim b-tiny">⌂</span>
              <span className="b-chip-name">Overview</span>
            </button>
            <button
              type="button"
              onClick={() => setSelected('general')}
              className={`b-chip${isGeneral ? ' is-active' : ''}`}
            >
              <span className="b-mono-dim b-tiny">◈</span>
              <span className="b-chip-name">General</span>
            </button>
          </div>
          <div className="ad-list-search">
            <span className="b-mono-dim">/</span>
            <input
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="filter sports…"
              className="ad-list-search-input"
              spellCheck={false}
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter('')}
                className="ad-list-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          {!catalog
            ? <p className="b-mono-dim" style={{ padding: '0 16px' }}>LOADING…</p>
            : grouped.size === 0
              ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
              : [...grouped.entries()].map(([cat, items]) => {
                  const open = effectiveExpanded.has(cat)
                  return (
                    <div key={cat} className={`ad-list-group${open ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(cat)}
                        className="ad-list-group-head"
                        aria-expanded={open}
                      >
                        <span className="ad-list-group-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                        <span className="ad-list-group-label">{cat}</span>
                        <span className="ad-list-group-count">{items.length}</span>
                      </button>
                      {open && items.map((s, i) => {
                        const active = s.slug === selected
                        const num = String(i + 1).padStart(2, '0')
                        return (
                          <button
                            type="button"
                            key={s.slug}
                            onClick={() => setSelected(s.slug)}
                            className={`b-sport-row${active ? ' is-active' : ''}`}
                          >
                            <span className="b-sport-num">{num}</span>
                            <span className="b-sport-name">{s.name}</span>
                            {sportIndicator(s)}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
          {/* SPORTSBOOKS — operator-keyed embeds (carried sports, availability,
              map). Same accordion row as the sport categories, after them. */}
          {showBooksGroup && (
            <div className={`ad-list-group${booksGroupOpen ? ' is-open' : ''}`}>
              <button
                type="button"
                onClick={() => toggleExpanded('SPORTSBOOKS')}
                className="ad-list-group-head"
                aria-expanded={booksGroupOpen}
              >
                <span className="ad-list-group-caret" aria-hidden="true">{booksGroupOpen ? '▾' : '▸'}</span>
                <span className="ad-list-group-label">SPORTSBOOKS</span>
                <span className="ad-list-group-count">{filteredBooks.length}</span>
              </button>
              {booksGroupOpen && filteredBooks.map((b, i) => {
                const active = selected === BOOK_PREFIX + b.slug
                return (
                  <button
                    type="button"
                    key={b.slug}
                    onClick={() => setSelected(BOOK_PREFIX + b.slug)}
                    className={`b-sport-row${active ? ' is-active' : ''}`}
                  >
                    <span className="b-sport-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="b-sport-name">{b.name}</span>
                    {bookKindTag(b)}
                  </button>
                )
              })}
            </div>
          )}
          <div
            className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
            onMouseDown={sidebar.onMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
          />
        </aside>

        {/* MAIN */}
        <main className="b-main">
          {/* OVERVIEW — the embed home: coverage state + the multi-sport
              odds dashboard as a first-class embeddable widget. */}
          {isOverview && (
            <>
              <div className="b-title-block">
                <div>
                  <div className="b-bread">
                    <span className="b-mono-dim">embed /</span>
                    <span className="b-mono">overview</span>
                  </div>
                  <h2 className="b-h2">Embeds Overview</h2>
                </div>
              </div>

              {/* Stats → event calendar → dashboard, one column. The calendar
                  (week grid of the AltSportsData upcoming events, one chip per
                  sport per day) sits between the coverage stats and the
                  dashboard for roles at or above EVENT_CALENDAR_MIN_ROLE. */}
              <div className="ew-overview-grid">
                <div className="ew-overview-main">
                  {/* EMBED COVERAGE — unboxed on purpose: this is the page's own
                      state-of-embeds readout, not an embeddable asset, so it
                      doesn't wear the ew-card frame the widget cards do. */}
                  <div className="ew-overview">
                    {!catalog
                      ? <p className="b-mono-dim">LOADING…</p>
                      : (
                          <>
                            <div className="ew-stats">
                              <div className="ew-stat">
                                <span className="ew-stat-n">{liveSports.length}</span>
                                <span className="b-mono-dim b-tiny">LIVE ODDS NOW</span>
                              </div>
                              <div className="ew-stat">
                                <span className="ew-stat-n">{liveSports.length + quietSports.length}</span>
                                <span className="b-mono-dim b-tiny">ODDS CONFIGS</span>
                              </div>
                              <div className="ew-stat">
                                <span className="ew-stat-n">{catalog.length}</span>
                                <span className="b-mono-dim b-tiny">EMBEDDABLE SPORTS</span>
                              </div>
                            </div>
                          </>
                        )}
                  </div>

                  {/* EVENT CALENDAR — week grid of upcoming events from the
                      AltSportsData API: one chip per sport per day, a sports
                      legend that filters, the selected day listed underneath.
                      Read-only, not an embed. */}
                  {showCalendar && (
                    <EventCalendar
                      calendar={calendar}
                      error={calendarError}
                      days={calendarDays}
                      onDays={setCalendarDays}
                      catalog={catalog}
                      onSelect={setSelected}
                    />
                  )}

                  {/* ODDS DASHBOARD — every live sport in one widget with a
                      filter bar, the real SDK mounted directly on the page and
                      skinned native (portal palette through --lp-* variables,
                      structural overrides injected into its shadow roots —
                      bet-slip overlay included). No iframe, no card frame, no
                      label: the overview goes stats → dashboard. */}
                  <div ref={dashMount} className="ew-dash-mount" />
                </div>
              </div>
            </>
          )}

          {/* SPORTSBOOK PAGE — the operator twins of the sport cards: the
              sports it carries (distribution SDK), where it is live
              (approvals SDK), and its availability map. The map takes the
              hero slot, like a catalog-only sport. */}
          {isBook && book && (
            <>
              <div className="b-title-block">
                <div>
                  <div className="b-bread">
                    <span className="b-mono-dim">embed /</span>
                    <span className="b-mono">sportsbook</span>
                    <span className="b-mono-dim">/</span>
                    <span className="b-mono">{book.slug}</span>
                  </div>
                  <h2 className="b-h2 ew-book-title">
                    {(book.logo_url || book.brand_color) && (
                      <span
                        className={`ew-book-mark${book.logo_fill ? ' is-fill' : ''}`}
                        style={{ background: book.brand_color ?? 'transparent' }}
                        aria-hidden="true"
                      >
                        {book.logo_url
                          ? <img src={assetUrl(book.logo_url)} alt="" loading="lazy" />
                          : <span>{book.name.slice(0, 2).toUpperCase()}</span>}
                      </span>
                    )}
                    {book.name}
                  </h2>
                </div>
                {affPicker}
              </div>

              <div className="ew-cards is-book">
                {/* CARRIED SPORTS */}
                <section className="ew-card ew-card-distribution">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ CARRIED SPORTS</span>
                    <div className="ew-card-head-r">
                      <button
                        type="button"
                        onClick={() => setDistTop5(v => !v)}
                        className={`ew-card-tab${distTop5 ? ' is-active' : ''}`}
                        title="Cap the grid at the first 5 sports"
                      >
                        TOP 5
                      </button>
                      <button type="button" onClick={() => setDistTab('preview')} className={`ew-card-tab${distTab === 'preview' ? ' is-active' : ''}`}>
                        PREVIEW
                      </button>
                      <button type="button" onClick={() => setDistTab('embed')} className={`ew-card-tab${distTab === 'embed' ? ' is-active' : ''}`}>
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {distTab === 'preview'
                    ? (
                        <div className="ew-card-body">
                          <SdkDistributionWidget
                            key={book.slug}
                            sportsbook={book.slug}
                            apiBase={API_BASE}
                            maxBooks={distTop5 ? 5 : undefined}
                          />
                        </div>
                      )
                    : <EmbedCode snippet={bookDistSnippet} copied={copiedKey === 'bdist'} onCopy={() => copyText('bdist', bookDistSnippet)} />}
                </section>

                {/* AVAILABILITY */}
                <section className="ew-card ew-card-approvals">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ AVAILABILITY</span>
                    <div className="ew-card-head-r">
                      <button type="button" onClick={() => setApprTab('preview')} className={`ew-card-tab${apprTab === 'preview' ? ' is-active' : ''}`}>
                        PREVIEW
                      </button>
                      <button type="button" onClick={() => setApprTab('embed')} className={`ew-card-tab${apprTab === 'embed' ? ' is-active' : ''}`}>
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {apprTab === 'preview'
                    ? (
                        <div className="ew-card-body">
                          <SdkApprovalsWidget key={book.slug} sportsbook={book.slug} apiBase={API_BASE} />
                        </div>
                      )
                    : <EmbedCode snippet={bookApprSnippet} copied={copiedKey === 'bappr'} onCopy={() => copyText('bappr', bookApprSnippet)} />}
                </section>

                {/* AVAILABILITY MAP — hero slot */}
                <section className="ew-card ew-card-map">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ AVAILABILITY MAP</span>
                    <div className="ew-card-head-r">
                      <button type="button" onClick={() => setMapTab('preview')} className={`ew-card-tab${mapTab === 'preview' ? ' is-active' : ''}`}>
                        PREVIEW
                      </button>
                      <button type="button" onClick={() => setMapTab('embed')} className={`ew-card-tab${mapTab === 'embed' ? ' is-active' : ''}`}>
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {mapTab === 'preview'
                    ? (
                        <div className="ew-card-body ew-map-body">
                          {mapEmpty
                            ? <p className="ew-map-empty b-mono-dim">NO MAP DATA — no states or regions recorded for this book</p>
                            : (
                                <ApprovalMapWidget
                                  key={`book:${book.slug}`}
                                  sportsbook={book.slug}
                                  apiBase={API_BASE}
                                  theme={isDark ? 'dark' : 'light'}
                                  world={WORLD}
                                  onEmpty={() => setMapEmpty(true)}
                                />
                              )}
                        </div>
                      )
                    : <EmbedCode snippet={bookMapSnippet} copied={copiedKey === 'bmap'} onCopy={() => copyText('bmap', bookMapSnippet)} />}
                </section>
              </div>
            </>
          )}
          {isBook && !book && bookCatalog && (
            <p className="b-mono-dim">SPORTSBOOK NOT FOUND — pick one from the SPORTSBOOKS group</p>
          )}

          {!isOverview && sport && (
            <>
              <div className="b-title-block">
                <div>
                  <div className="b-bread">
                    <span className="b-mono-dim">embed /</span>
                    {isGeneral
                      ? <span className="b-mono">general — all books</span>
                      : (
                          <>
                            <span className="b-mono">{categoryLabel(sport.category).toLowerCase()}</span>
                            <span className="b-mono-dim">/</span>
                            <span className="b-mono">{sport.slug}</span>
                          </>
                        )}
                  </div>
                  <h2 className="b-h2">{sport.name}</h2>
                </div>
                {affPicker}
              </div>

              {/* UPCOMING EVENTS — this sport's next events from the AltSportsData
                  calendar (same payload the Overview column uses), read-only.
                  GENERAL has no league of its own. */}
              {showCalendar && !isGeneral && (
                <EventCalendar
                  variant="sport"
                  sportSlug={sport.slug}
                  calendar={calendar}
                  error={calendarError}
                  days={calendarDays}
                  onDays={setCalendarDays}
                  catalog={catalog}
                  onSelect={setSelected}
                />
              )}

              {/*
                Widget cards per sport. Live Odds spans both rows on the left
                when the sport has an odds config; for catalog-only sports the
                AVAILABILITY MAP takes that hero slot instead (is-no-odds).
                GENERAL renders Distribution + Map in single-column flow.
              */}
              <div className={`ew-cards${isGeneral ? ' is-general' : ''}${!isGeneral && !hasOdds ? ' is-no-odds' : ''}`}>
                {/* LIVE ODDS — only for sports with an odds config */}
                {hasOdds && (
                <section className="ew-card ew-card-odds">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ LIVE ODDS</span>
                    <div className="ew-card-head-r">
                      <button
                        type="button"
                        onClick={() => setTab('preview')}
                        className={`ew-card-tab${tab === 'preview' ? ' is-active' : ''}`}
                      >
                        PREVIEW
                      </button>
                      <button
                        type="button"
                        onClick={() => setTab('embed')}
                        className={`ew-card-tab${tab === 'embed' ? ' is-active' : ''}`}
                      >
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {tab === 'preview'
                    ? (
                        <div className="b-iframe-wrap">
                          {!iframeLoaded && (
                            <div className="b-iframe-skeleton" aria-hidden="true">
                              <span className="b-dot b-dot-on" />
                              <span className="b-mono">LOADING WIDGET…</span>
                            </div>
                          )}
                          <iframe
                            title={`${sport.name} preview`}
                            srcDoc={previewDoc}
                            className="b-iframe"
                            sandbox="allow-scripts allow-same-origin allow-popups"
                            onLoad={() => setIframeLoaded(true)}
                          />
                        </div>
                      )
                    : (
                        <div className="ew-embed-body">
                          <div className="ew-embed-meta">
                            <span className="b-mono-dim">{snippet.split('\n').length} LINES</span>
                            <button
                              type="button"
                              onClick={() => copyText('odds', snippet)}
                              className={`b-btn b-btn-primary b-btn-tiny${copiedKey === 'odds' ? ' is-success' : ''}`}
                            >
                              {copiedKey === 'odds' ? '✓ COPIED' : 'COPY'}
                            </button>
                          </div>
                          <pre className="b-code">
                            {snippet.split('\n').map((line, i) => (
                              <div key={i} className="b-code-line">
                                <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
                                <span className="b-code-text">{highlightLine(line, i)}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      )}
                </section>
                )}

                {/* APPROVALS — hidden for GENERAL (approvals are sport-scoped) */}
                {!isGeneral && (
                <section className="ew-card ew-card-approvals">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ APPROVALS</span>
                    <div className="ew-card-head-r">
                      <button
                        type="button"
                        onClick={() => setApprTab('preview')}
                        className={`ew-card-tab${apprTab === 'preview' ? ' is-active' : ''}`}
                      >
                        PREVIEW
                      </button>
                      <button
                        type="button"
                        onClick={() => setApprTab('embed')}
                        className={`ew-card-tab${apprTab === 'embed' ? ' is-active' : ''}`}
                      >
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {apprTab === 'preview'
                    ? (
                        <div className="ew-card-body">
                          <ApprovalsWidget sport={details} loading={detailsLoading} />
                        </div>
                      )
                    : (
                        <div className="ew-embed-body">
                          <div className="ew-embed-meta">
                            <span className="b-mono-dim">{apprSnippet.split('\n').length} LINES</span>
                            <button
                              type="button"
                              onClick={() => copyText('appr', apprSnippet)}
                              className={`b-btn b-btn-primary b-btn-tiny${copiedKey === 'appr' ? ' is-success' : ''}`}
                            >
                              {copiedKey === 'appr' ? '✓ COPIED' : 'COPY'}
                            </button>
                          </div>
                          <pre className="b-code">
                            {apprSnippet.split('\n').map((line, i) => (
                              <div key={i} className="b-code-line">
                                <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
                                <span className="b-code-text">{highlightLine(line, i)}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      )}
                </section>
                )}

                {/* DISTRIBUTION */}
                <section className="ew-card ew-card-distribution">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ DISTRIBUTION</span>
                    <div className="ew-card-head-r">
                      <button
                        type="button"
                        onClick={() => setDistTop5(v => !v)}
                        className={`ew-card-tab${distTop5 ? ' is-active' : ''}`}
                        title="Cap the grid at the top 5 books"
                      >
                        TOP 5
                      </button>
                      <button
                        type="button"
                        onClick={() => setDistTab('preview')}
                        className={`ew-card-tab${distTab === 'preview' ? ' is-active' : ''}`}
                      >
                        PREVIEW
                      </button>
                      <button
                        type="button"
                        onClick={() => setDistTab('embed')}
                        className={`ew-card-tab${distTab === 'embed' ? ' is-active' : ''}`}
                      >
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {distTab === 'preview'
                    ? (
                        <div className="ew-card-body">
                          <DistributionWidget sport={details} loading={detailsLoading} maxBooks={distTop5 ? 5 : undefined} />
                        </div>
                      )
                    : (
                        <div className="ew-embed-body">
                          <div className="ew-embed-meta">
                            <span className="b-mono-dim">{distSnippet.split('\n').length} LINES</span>
                            <button
                              type="button"
                              onClick={() => copyText('dist', distSnippet)}
                              className={`b-btn b-btn-primary b-btn-tiny${copiedKey === 'dist' ? ' is-success' : ''}`}
                            >
                              {copiedKey === 'dist' ? '✓ COPIED' : 'COPY'}
                            </button>
                          </div>
                          <pre className="b-code">
                            {distSnippet.split('\n').map((line, i) => (
                              <div key={i} className="b-code-line">
                                <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
                                <span className="b-code-text">{highlightLine(line, i)}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      )}
                </section>

                {/* AVAILABILITY MAP — full-width row below the other cards,
                    or the hero slot when the sport has no odds config */}
                <section className="ew-card ew-card-map">
                  <div className="ew-card-head">
                    <span className="b-mono">▣ AVAILABILITY MAP</span>
                    <div className="ew-card-head-r">
                      <button
                        type="button"
                        onClick={() => setMapTab('preview')}
                        className={`ew-card-tab${mapTab === 'preview' ? ' is-active' : ''}`}
                      >
                        PREVIEW
                      </button>
                      <button
                        type="button"
                        onClick={() => setMapTab('embed')}
                        className={`ew-card-tab${mapTab === 'embed' ? ' is-active' : ''}`}
                      >
                        EMBED CODE
                      </button>
                    </div>
                  </div>
                  {mapTab === 'preview'
                    ? (
                        <div className="ew-card-body ew-map-body">
                          {mapEmpty
                            ? <p className="ew-map-empty b-mono-dim">NO MAP DATA — approvals not populated for this sport</p>
                            : (
                                <ApprovalMapWidget
                                  key={`${sport.slug}:${affFlag ?? ''}`}
                                  sport={sport.slug}
                                  apiBase={API_BASE}
                                  theme={isDark ? 'dark' : 'light'}
                                  world={WORLD}
                                  subAffiliate={affFlag}
                                  onEmpty={() => setMapEmpty(true)}
                                />
                              )}
                        </div>
                      )
                    : (
                        <div className="ew-embed-body">
                          <div className="ew-embed-meta">
                            <span className="b-mono-dim">{mapSnippet.split('\n').length} LINES</span>
                            <button
                              type="button"
                              onClick={() => copyText('map', mapSnippet)}
                              className={`b-btn b-btn-primary b-btn-tiny${copiedKey === 'map' ? ' is-success' : ''}`}
                            >
                              {copiedKey === 'map' ? '✓ COPIED' : 'COPY'}
                            </button>
                          </div>
                          <pre className="b-code">
                            {mapSnippet.split('\n').map((line, i) => (
                              <div key={i} className="b-code-line">
                                <span className="b-code-ln">{String(i + 1).padStart(2, '0')}</span>
                                <span className="b-code-text">{highlightLine(line, i)}</span>
                              </div>
                            ))}
                          </pre>
                        </div>
                      )}
                </section>
              </div>
            </>
          )}
          {!isOverview && !isBook && !sport && catalog && (
            <p className="b-mono-dim">SELECT A SPORT TO BEGIN</p>
          )}
        </main>
      </div>

      {/* MOBILE BOTTOM SHEET — the shared sport picker (SportSheet.tsx) */}
      {sheetOpen && (
        <SportSheet
          title="SELECT SPORT"
          items={[
            {
              key: 'overview',
              label: 'Overview',
              right: <span className="b-mono-dim b-tiny">HOME</span>,
              pinned: true,
            },
            {
              key: 'general',
              label: 'General — all books',
              right: <span className="b-mono-dim b-tiny">ALL BOOKS</span>,
              pinned: true,
            },
            ...(catalog ?? []).map(s => ({
              key: s.slug,
              label: s.name,
              category: s.category ?? undefined,
              right: sportIndicator(s),
            })),
            // Books group under their own heading after the sport categories
            // (categoryLabel falls through to the uppercased raw value).
            ...(bookCatalog ?? []).map(b => ({
              key: BOOK_PREFIX + b.slug,
              label: b.name,
              category: 'Sportsbooks',
              sub: b.slug,
              right: bookKindTag(b),
            })),
          ]}
          selectedKey={selected}
          onSelect={(slug) => {
            setSelected(slug)
            setSheetOpen(false)
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </>
  )
}
